/* Compatibility additions for the Elideia local core and Aveglow 1.7 templates.
   These are replacement implementations, not Shrine's missing 1.7 runtime. */
function selectedCompatibilityUpsells(quantityBreaks) {
  const selected = quantityBreaks.querySelector('input[name="quantity"]:checked');
  const offer = selected && (selected.nextElementSibling?.matches('.quantity-break') ? selected.nextElementSibling : quantityBreaks.querySelector(`label[for="${CSS.escape(selected.id)}"]`));
  if (!offer) return [];
  return Array.from(offer.querySelectorAll('.quantity-break__upsell[data-selected="true"]'), el => el.dataset.product).filter(Boolean);
}
function applyCompatibilitySellingPlan(data, productForm) {
  const plan = data.get('selling_plan');
  if (!plan) return;
  // Keep plans off gifts/upsells, which can belong to unrelated products.
  const mainIds = new Set([String(productForm.formIdInput.value), ...(productForm.quantityBreaks?.formVariants || []).map(String)]);
  let hasItems = false;
  for (const [key, value] of Array.from(data.entries())) {
    const match = key.match(/^items\[(\d+)\]\[id\]$/);
    if (match) { hasItems = true; if (mainIds.has(String(value))) data.set(`items[${match[1]}][selling_plan]`, plan); }
  }
  if (hasItems) data.delete('selling_plan');
}
class CompatibilityCardVariants extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.variants = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '[]');
    this.addEventListener('change', () => this.update());
    this.update();
  }
  update() {
    const values = Array.from(this.querySelectorAll('.product-form__input'), group => group.querySelector('select')?.value || group.querySelector('input:checked')?.value);
    const variant = this.variants.find(v => v.options.every((value, index) => value === values[index]));
    const card = this.closest('.card-wrapper') || this.parentElement;
    const form = document.getElementById(this.dataset.section) || card.querySelector('form[action*="/cart/add"]');
    const input = form?.querySelector('[name="id"]');
    if (input && variant) { input.value = variant.id; input.dispatchEvent(new Event('change', {bubbles: true})); }
    const button = form?.querySelector('[type="submit"]');
    if (button) button.disabled = !variant?.available;
    if (!variant) return;
    card.querySelectorAll('a[href*="/products/"]').forEach(link => {
      const url = new URL(link.href, location.origin); url.searchParams.set('variant', variant.id); link.href = url.href;
    });
    const img = card.querySelector('.card__media img');
    if (img && variant.featured_image?.src) { img.src = variant.featured_image.src; img.removeAttribute('srcset'); }
    this.dispatchEvent(new CustomEvent('compatibility:variant-change', {bubbles: true, detail: {variant}}));
  }
}
customElements.define('card-variant-selects', CompatibilityCardVariants);
class CompatibilityQuantityUpsells extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.querySelectorAll('.quantity-break__upsell[data-toggle="true"]').forEach(item => {
      item.tabIndex = 0; item.setAttribute('role', 'checkbox'); item.setAttribute('aria-checked', item.dataset.selected === 'true');
      const toggle = event => {
        if (event.target.closest('form, button, a, select, input')) return;
        event.preventDefault();
        item.dataset.selected = item.dataset.selected === 'true' ? 'false' : 'true';
        item.setAttribute('aria-checked', item.dataset.selected);
        item.dispatchEvent(new CustomEvent('compatibility:upsell-change', {bubbles: true}));
      };
      item.addEventListener('click', toggle);
      item.addEventListener('keydown', event => { if (event.key === ' ' || event.key === 'Enter') toggle(event); });
    });
  }
}
customElements.define('quantity-break-upsells', CompatibilityQuantityUpsells);
class CompatibilitySubscription extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.selected = this.dataset.preselected || 'one_time';
    this.querySelectorAll('.subscription__box').forEach(box => {
      box.setAttribute('role', 'group');
      // Avoid taking keyboard focus away from frequency inputs.
      box.tabIndex = 0;
      box.addEventListener('click', event => {
        if (event.target.closest('select, option, input, a, button')) return;
        this.selected = this.dataset.isCheckbox === 'true' && this.selected === 'subscription' ? 'one_time' : box.dataset.type;
        this.update();
      });
      box.addEventListener('keydown', event => {
        if (event.target !== box || !['Enter', ' '].includes(event.key)) return;
        event.preventDefault(); box.click();
      });
    });
    this.addEventListener('change', () => this.update());
    this.unsubscribe = typeof subscribe === 'function' ? subscribe(PUB_SUB_EVENTS.variantChange, event => {
      if (String(event.data?.sectionId) === this.dataset.section) this.updateVariant(event.data.variant);
    }) : null;
    // Main runtime is deferred after this file; subscribe after all deferred scripts.
    if (!this.unsubscribe) document.addEventListener('DOMContentLoaded', () => {
      if (!this.isConnected || this.unsubscribe || typeof subscribe !== 'function') return;
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
        if (String(event.data?.sectionId) === this.dataset.section) this.updateVariant(event.data.variant);
      });
    }, {once: true});
    this.update();
  }
  disconnectedCallback() { this.unsubscribe?.(); this.unsubscribe = null; }
  updateVariant(variant) {
    if (!variant) return;
    this.dataset.basePrice = variant.price;
    this.dataset.baseComparePrice = variant.compare_at_price || variant.price;
    // Full variant allocations are embedded by the build to prevent invalid plans.
    const all = JSON.parse(this.querySelector('script[data-compatibility-allocations]')?.textContent || '[]');
    this.allocations = all.find(v => String(v.id) === String(variant.id))?.selling_plan_allocations || [];
    this.querySelectorAll('[data-selling-plan-id]').forEach(input => {
      const allocation = this.allocations.find(a => String(a.selling_plan_id || a.selling_plan?.id) === input.dataset.sellingPlanId);
      input.disabled = !allocation;
      if (allocation) { input.dataset.price = allocation.price; input.dataset.comparePrice = allocation.compare_at_price || variant.price; }
    });
    const select = this.querySelector('select');
    if (select?.selectedOptions[0]?.disabled) select.value = Array.from(select.options).find(o => !o.disabled)?.value || '';
    const radio = this.querySelector('input[type="radio"]:checked');
    if (radio?.disabled) { radio.checked = false; const next = this.querySelector('input[type="radio"]:not(:disabled)'); if (next) next.checked = true; }
    this.update();
  }
  update() {
    const selectedInput = this.querySelector('select')?.selectedOptions[0] || this.querySelector('input[type="radio"]:checked');
    let plan = selectedInput?.dataset.sellingPlanId || this.dataset.firstSellingPlanId;
    if (selectedInput?.disabled || (this.allocations && !this.allocations.some(a => String(a.selling_plan_id || a.selling_plan?.id) === String(plan)))) plan = '';
    if (this.selected === 'subscription' && !plan) this.selected = 'one_time';
    const form = document.getElementById(`product-form-${this.dataset.section}`);
    if (form) {
      let input = form.querySelector('[name="selling_plan"]');
      if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'selling_plan'; form.append(input); }
      input.value = this.selected === 'subscription' ? plan : '';
      input.disabled = !input.value;
    }
    this.querySelectorAll('.subscription__box').forEach(box => {
      const selected = box.dataset.type === this.selected;
      box.dataset.selected = String(selected); box.classList.toggle('subscription__box--selected', selected);
    });
    const base = Number(this.dataset.basePrice || 0);
    const compare = Number(this.dataset.baseComparePrice || base);
    const price = Number(selectedInput?.dataset.price || this.discountedPrice(base));
    this.querySelectorAll('.one-time-price-update, .sub-price-update').forEach(el => {
      const subscription = el.classList.contains('sub-price-update');
      const current = subscription ? price : base;
      const saved = Math.max(0, compare - current);
      const amounts = {price: this.money(current), compare_price: this.money(compare), amount_saved: this.money(saved), amount_saved_rounded: this.money(Math.round(saved / 100) * 100), percentage_saved: compare ? String(Math.round(saved / compare * 100)) : '0'};
      if (el.dataset.text) el.textContent = el.dataset.text.replace(/\[([a-z_]+)\]/g, (match, name) => amounts[name] ?? match);
    });
  }
  discountedPrice(base) {
    const value = Number(this.dataset.discountValue || 0);
    if (this.dataset.discountType === 'percentage') return Math.round(base * (1 - value / 100));
    if (this.dataset.discountType === 'fixed_amount') return Math.max(0, base - value);
    return value || base;
  }
  money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
      let value = (Number(cents) / 100).toFixed(token.includes('no_decimals') ? 0 : 2);
      const parts = value.split('.');
      const commaDecimal = token.includes('comma_separator');
      const separator = token.includes('space_separator') ? ' ' : commaDecimal ? '.' : ',';
      return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator) + (parts[1] ? (commaDecimal ? ',' : '.') + parts[1] : '');
    }).replace(/<[^>]*>/g, '');
  }
}
customElements.define('shrine-subscription-widget', CompatibilitySubscription);
